import { memo, useMemo } from 'react';
import FieldToggle from './FieldToggle';
import {
    AfterSelectionOptions,
    PuppetQuestion,
    QuestionType,
} from '@synergetx/puppet-interview-types';
import { Box, Grid, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { useField } from 'formik';
import RequiredField from './RequiredField';

type Props = {
    position: number;
};

const QuestionTypeSpecificSettings = ({ position }: Props) => {
    const [{ value: question }, , { setValue: setQuestion }] = useField<PuppetQuestion>(
        `questions[${position}]`,
    );
    const isPuppetQuestion = question?.type === QuestionType.PUPPET;

    // Hide the required toggle for question types that are not interactive (notes) or always required (puppet)
    const showRequiredToggle = useMemo(
        () =>
            !(
                question.type &&
                [QuestionType.PUPPET, QuestionType.INFO, QuestionType.CONTINUE].includes(
                    question.type,
                )
            ),
        [question.type],
    );

    if (isPuppetQuestion) {
        return (
            <>
                {/* Puppet Response Section */}
                <Grid size={12} sx={{ mt: 1.5 }}>
                    <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', fontWeight: 500, mb: 0.5, display: 'block' }}
                    >
                        Puppet Behavior
                    </Typography>
                </Grid>

                {/* Reply after selection */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Tooltip
                        title="Puppet may acknowledge after participant selects a response"
                        placement="top"
                    >
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.75, color: 'text.secondary' }}>
                                Reply after selection
                            </Typography>
                            <ToggleButtonGroup
                                size="small"
                                color="primary"
                                exclusive
                                value={question.afterSelection}
                                aria-label="Puppet reply option"
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e, value: AfterSelectionOptions) => {
                                    if (value !== null)
                                        setQuestion({ ...question, afterSelection: value });
                                }}
                                sx={{
                                    '& .MuiToggleButton-root': {
                                        textTransform: 'none',
                                        px: 1.5,
                                        py: 0.5,
                                    },
                                }}
                            >
                                <ToggleButton value="meToo">Me too!</ToggleButton>
                                <ToggleButton value="thatsLikeMe">That's like me!</ToggleButton>
                                <ToggleButton value="none">None</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    </Tooltip>
                </Grid>

                {/* Response collection settings */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                        }}
                    >
                        <Tooltip
                            title="Introduce animated puppet without collecting a response"
                            placement="right"
                        >
                            <span>
                                <FieldToggle
                                    question={question}
                                    position={position}
                                    fieldName={'noSelectionIntroQuestion'}
                                    label="Skip collecting response"
                                />
                            </span>
                        </Tooltip>
                        <Tooltip
                            title="After both puppets speak, play 'Which one of us is more like you?'"
                            placement="right"
                        >
                            <span>
                                <FieldToggle
                                    question={question}
                                    position={position}
                                    fieldName={'askWhichOne'}
                                    label="Ask 'Which one is more like you?'"
                                />
                            </span>
                        </Tooltip>
                        <Tooltip
                            title="Show celebratory stars animation after selection"
                            placement="right"
                        >
                            <span>
                                <FieldToggle
                                    question={question}
                                    position={position}
                                    fieldName={'showStars'}
                                    label="Show stars animation"
                                />
                            </span>
                        </Tooltip>
                    </Box>
                </Grid>
            </>
        );
    } else if (showRequiredToggle) {
        return (
            <Grid
                size={6}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <RequiredField position={position} />
            </Grid>
        );
    }

    return null;
};

export default memo(QuestionTypeSpecificSettings);
